import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export interface AdminClubStats {
    total: number;
    pending_count: number;
    approved_count: number;
    rejected_count: number;
    reports_open: number;
}

export interface AdminClubRow {
    id: string;
    created_by: string;
    creator_username: string;
    creator_email: string;
    name: string;
    description: string;
    country_code: string;
    visibility: string;
    status: string;
    verified: boolean;
    initials: string;
    created_at: string;
    updated_at: string;
}

export interface AdminClubReportRow {
    report_id: string;
    club_id: string;
    club_name: string;
    reporter_id: string;
    reporter_username: string;
    reason: string;
    details: string | null;
    status: string;
    created_at: string;
    admin_notes: string | null;
}

export function useAdminFishingClubsStats() {
    return useQuery<AdminClubStats>({
        queryKey: ['admin', 'fishing-clubs', 'stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_fishing_clubs_stats');
            if (error) throw new Error(error.message);
            const row = normalizeRpcResponse(data)[0] as Record<string, unknown>;
            return {
                total: Number(row?.total ?? 0),
                pending_count: Number(row?.pending_count ?? 0),
                approved_count: Number(row?.approved_count ?? 0),
                rejected_count: Number(row?.rejected_count ?? 0),
                reports_open: Number(row?.reports_open ?? 0),
            };
        },
        staleTime: 15_000,
    });
}

export function useAdminFishingClubsList(statusFilter: string, search: string) {
    return useQuery<AdminClubRow[]>({
        queryKey: ['admin', 'fishing-clubs', 'list', statusFilter, search],
        queryFn: async () => {
            const params: Record<string, unknown> = { p_limit: 300, p_offset: 0 };
            if (statusFilter !== 'all') params.p_status = statusFilter;
            if (search.trim()) params.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_fishing_clubs_list', params);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data) as Record<string, unknown>[];
            return rows.map((r) => ({
                id: String(r.id),
                created_by: String(r.created_by),
                creator_username: String(r.creator_username ?? ''),
                creator_email: String(r.creator_email ?? ''),
                name: String(r.name ?? ''),
                description: String(r.description ?? ''),
                country_code: String(r.country_code ?? ''),
                visibility: String(r.visibility ?? ''),
                status: String(r.status ?? ''),
                verified: Boolean(r.verified),
                initials: String(r.initials ?? ''),
                created_at: String(r.created_at ?? ''),
                updated_at: String(r.updated_at ?? ''),
            }));
        },
        staleTime: 15_000,
    });
}

export function useAdminFishingClubSetStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: { id: string; status: string; rejectionReason?: string }) => {
            const { error } = await dbClient.rpc('admin_fishing_club_set_status', {
                p_id: args.id,
                p_status: args.status,
                p_rejection_reason: args.rejectionReason ?? null,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
        },
    });
}

export function useAdminFishingClubDelete() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { error } = await dbClient.rpc('admin_fishing_club_delete', { p_id: id });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
        },
    });
}

export function useAdminFishingClubCreate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: Record<string, unknown>) => {
            const { data, error } = await dbClient.rpc('admin_fishing_club_create', payload);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data)[0];
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
        },
    });
}

export function useAdminFishingClubReportsList(status: string) {
    return useQuery<AdminClubReportRow[]>({
        queryKey: ['admin', 'fishing-club-reports', status],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_fishing_club_reports_list', {
                p_status: status === 'all' ? null : status,
                p_limit: 200,
                p_offset: 0,
            });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data) as Record<string, unknown>[];
            return rows.map((r) => ({
                report_id: String(r.report_id),
                club_id: String(r.club_id),
                club_name: String(r.club_name ?? ''),
                reporter_id: String(r.reporter_id),
                reporter_username: String(r.reporter_username ?? ''),
                reason: String(r.reason ?? ''),
                details: r.details != null ? String(r.details) : null,
                status: String(r.status ?? ''),
                created_at: String(r.created_at ?? ''),
                admin_notes: r.admin_notes != null ? String(r.admin_notes) : null,
            }));
        },
        staleTime: 15_000,
    });
}

export function useAdminFishingClubReportSetStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: { reportId: string; status: string; notes?: string }) => {
            const { error } = await dbClient.rpc('admin_fishing_club_report_set_status', {
                p_report_id: args.reportId,
                p_status: args.status,
                p_admin_notes: args.notes ?? null,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-club-reports'] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
        },
    });
}

// ─── Detail + members ────────────────────────────────────────────────────────

export type ClubMemberRole = 'creator' | 'admin' | 'member';

export interface AdminClubDetail {
    id: string;
    created_by: string;
    creator_username: string;
    creator_email: string;
    name: string;
    description: string;
    external_url: string;
    logo_url: string | null;
    country_code: string;
    location_label: string;
    latitude: number | null;
    longitude: number | null;
    visibility: 'public' | 'secret' | string;
    water_types: string[];
    modalities: string[];
    location_slugs: string[];
    initials: string;
    verified: boolean;
    status: string;
    rejection_reason: string | null;
    club_kind: string | null;
    is_official: boolean;
    club_contact: string | null;
    web_slug: string | null;
    members_count: number;
    created_at: string;
    updated_at: string;
}

export interface AdminClubMemberRow {
    user_id: string;
    username: string;
    display_name: string;
    email: string;
    avatar_url: string | null;
    role: ClubMemberRole | string;
    can_invite: boolean;
    is_principal: boolean;
    joined_at: string;
    member_total: number;
}

function asStringArray(value: unknown): string[] {
    if (Array.isArray(value)) return value.map(String);
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed.map(String) : [];
        } catch {
            return [];
        }
    }
    return [];
}

function mapClubDetail(r: Record<string, unknown>): AdminClubDetail {
    return {
        id: String(r.id),
        created_by: String(r.created_by ?? ''),
        creator_username: String(r.creator_username ?? ''),
        creator_email: String(r.creator_email ?? ''),
        name: String(r.name ?? ''),
        description: String(r.description ?? ''),
        external_url: String(r.external_url ?? ''),
        logo_url: r.logo_url != null ? String(r.logo_url) : null,
        country_code: String(r.country_code ?? ''),
        location_label: String(r.location_label ?? ''),
        latitude: r.latitude != null ? Number(r.latitude) : null,
        longitude: r.longitude != null ? Number(r.longitude) : null,
        visibility: String(r.visibility ?? 'public'),
        water_types: asStringArray(r.water_types),
        modalities: asStringArray(r.modalities),
        location_slugs: asStringArray(r.location_slugs),
        initials: String(r.initials ?? ''),
        verified: Boolean(r.verified),
        status: String(r.status ?? ''),
        rejection_reason: r.rejection_reason != null ? String(r.rejection_reason) : null,
        club_kind: r.club_kind != null ? String(r.club_kind) : null,
        is_official: Boolean(r.is_official),
        club_contact: r.club_contact != null ? String(r.club_contact) : null,
        web_slug: r.web_slug != null ? String(r.web_slug) : null,
        members_count: Number(r.members_count ?? 0),
        created_at: String(r.created_at ?? ''),
        updated_at: String(r.updated_at ?? ''),
    };
}

export function useAdminFishingClubDetail(clubId: string | null) {
    return useQuery<AdminClubDetail | null>({
        queryKey: ['admin', 'fishing-clubs', 'detail', clubId],
        queryFn: async () => {
            if (!clubId) return null;
            const { data, error } = await dbClient.rpc('admin_fishing_club_get', { p_id: clubId });
            if (error) throw new Error(error.message);
            const row = normalizeRpcResponse(data)[0] as Record<string, unknown> | undefined;
            return row ? mapClubDetail(row) : null;
        },
        enabled: Boolean(clubId),
        staleTime: 15_000,
    });
}

export function useAdminFishingClubUpdate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: {
            id: string;
            name: string;
            description: string;
            external_url: string;
            /** Empty string clears logo; null keeps current (RPC COALESCE semantics). Prefer string. */
            logo_url: string | null;
            country_code: string;
            location_label: string;
            latitude: number | null;
            longitude: number | null;
            visibility: string;
            water_types: string[];
            modalities: string[];
            location_slugs: string[];
            initials: string;
            verified: boolean;
        }) => {
            const { error } = await dbClient.rpc('admin_fishing_club_update', {
                p_id: payload.id,
                p_name: payload.name,
                p_description: payload.description,
                p_external_url: payload.external_url,
                p_logo_url: payload.logo_url,
                p_country_code: payload.country_code,
                p_location_label: payload.location_label,
                p_latitude: payload.latitude,
                p_longitude: payload.longitude,
                p_visibility: payload.visibility,
                p_water_types: payload.water_types,
                p_modalities: payload.modalities,
                p_location_slugs: payload.location_slugs,
                p_initials: payload.initials,
                p_verified: payload.verified,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'detail', vars.id] });
        },
    });
}

export function useAdminFishingClubMembers(clubId: string | null, search: string) {
    return useQuery<AdminClubMemberRow[]>({
        queryKey: ['admin', 'fishing-clubs', 'members', clubId, search],
        queryFn: async () => {
            if (!clubId) return [];
            const { data, error } = await dbClient.rpc('admin_fishing_club_members_list', {
                p_club_id: clubId,
                p_search: search.trim() || null,
                p_limit: 500,
                p_offset: 0,
            });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data) as Record<string, unknown>[];
            return rows.map((r) => ({
                user_id: String(r.user_id),
                username: String(r.username ?? ''),
                display_name: String(r.display_name ?? ''),
                email: String(r.email ?? ''),
                avatar_url: r.avatar_url != null ? String(r.avatar_url) : null,
                role: String(r.role ?? 'member'),
                can_invite: Boolean(r.can_invite),
                is_principal: Boolean(r.is_principal),
                joined_at: String(r.joined_at ?? ''),
                member_total: Number(r.member_total ?? 0),
            }));
        },
        enabled: Boolean(clubId),
        staleTime: 10_000,
    });
}

export function useAdminFishingClubMemberAdd() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: { clubId: string; userId: string; role?: 'admin' | 'member' }) => {
            const { error } = await dbClient.rpc('admin_fishing_club_member_add', {
                p_club_id: args.clubId,
                p_user_id: args.userId,
                p_role: args.role ?? 'member',
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'members', vars.clubId] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'detail', vars.clubId] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
        },
    });
}

export function useAdminFishingClubMemberRemove() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: { clubId: string; userId: string }) => {
            const { error } = await dbClient.rpc('admin_fishing_club_member_remove', {
                p_club_id: args.clubId,
                p_user_id: args.userId,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'members', vars.clubId] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'detail', vars.clubId] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
        },
    });
}

export function useAdminFishingClubMemberSetRole() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: {
            clubId: string;
            userId: string;
            role: ClubMemberRole;
            canInvite?: boolean;
            isPrincipal?: boolean;
        }) => {
            const { error } = await dbClient.rpc('admin_fishing_club_member_set_role', {
                p_club_id: args.clubId,
                p_target: args.userId,
                p_role: args.role,
                p_can_invite: args.canInvite ?? null,
                p_is_principal: args.isPrincipal ?? null,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'members', vars.clubId] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'detail', vars.clubId] });
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs'] });
        },
    });
}

const ANNOUNCEMENT_VALIDITY_DAYS = [1, 3, 5, 15, 30, 90] as const;
export type AdminClubAnnouncementValidityDays = (typeof ANNOUNCEMENT_VALIDITY_DAYS)[number];

export function useAdminFishingClubAnnouncementPost() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: {
            clubId: string;
            body: string;
            validityDays?: AdminClubAnnouncementValidityDays;
        }) => {
            const validity = args.validityDays ?? 15;
            const { data, error } = await dbClient.rpc('admin_fishing_club_announcement_post', {
                p_club_id: args.clubId,
                p_body: args.body,
                p_validity_days: validity,
            });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data) as Record<string, unknown>[];
            const row = rows[0];
            return {
                id: row?.id != null ? String(row.id) : null,
                created_at: row?.created_at != null ? String(row.created_at) : null,
                expires_at: row?.expires_at != null ? String(row.expires_at) : null,
            };
        },
        onSuccess: (_d, vars) => {
            qc.invalidateQueries({ queryKey: ['admin', 'fishing-clubs', 'detail', vars.clubId] });
        },
    });
}
